import { useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "./ui/card";
import { AlertTriangle, Phone, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EscalationModal = ({ 
  isOpen, 
  onClose, 
  onContactCrisisLine,
  sessionId = null,
  triggerType = "auto_detected",
  triggerCategory = "emergency_escalation",
  volunteerId = null
}) => {
  // Log incident when modal opens
  useEffect(() => {
    if (isOpen) {
      logIncident();
    }
  }, [isOpen]);

  const logIncident = async () => {
    try {
      await axios.post(`${API}/incidents`, {
        session_id: sessionId,
        trigger_type: triggerType,
        trigger_category: triggerCategory,
        action_taken: "displayed_escalation_modal",
        volunteer_id: volunteerId,
        system_notes: "Escalation modal displayed to user"
      });
    } catch (error) {
      // Don't block the escalation flow if logging fails
      console.error("Failed to log incident:", error);
    }
  };

  const handleContactCrisisLine = async (line) => {
    // Log the escalation action
    try {
      await axios.post(`${API}/incidents`, {
        session_id: sessionId,
        trigger_type: triggerType,
        trigger_category: "emergency_escalation",
        action_taken: line === "988" ? "988_recommended" : "911_recommended",
        volunteer_id: volunteerId,
        system_notes: `User clicked ${line} contact button`
      });
    } catch (error) {
      console.error("Failed to log escalation action:", error);
    }
    
    if (onContactCrisisLine) {
      onContactCrisisLine(line);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white shadow-2xl border-0 border-t-4 border-t-destructive">
        <CardHeader className="text-center pb-2 relative">
          {onClose && (
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="text-xl font-bold text-destructive" style={{ fontFamily: 'Manrope' }}>
            It sounds like you may be in immediate danger.
          </h1>
        </CardHeader>
        
        <CardContent className="space-y-4 px-6">
          <p className="text-center text-muted-foreground">
            VALORLink volunteers <span className="font-semibold">cannot</span> provide emergency or clinical support.
          </p>

          <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-4">
            <p className="font-semibold text-foreground mb-3 text-center" style={{ fontFamily: 'Manrope' }}>
              Please contact help now:
            </p>
            <div className="space-y-3">
              {/* Veterans Crisis Hotline */}
              <a 
                href="tel:988"
                className="flex items-center gap-3 p-3 bg-white rounded-lg border border-border hover:border-primary hover:bg-primary/5 transition-all group"
                onClick={() => handleContactCrisisLine('988')}
              >
                <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                    Veterans Crisis Hotline
                  </p>
                  <p className="text-lg font-bold text-primary">988 (Press 1)</p>
                </div>
              </a>

              {/* Emergency Services */}
              <a 
                href="tel:911"
                className="flex items-center gap-3 p-3 bg-white rounded-lg border border-border hover:border-destructive hover:bg-destructive/5 transition-all group"
                onClick={() => handleContactCrisisLine('911')}
              >
                <div className="w-10 h-10 bg-destructive rounded-full flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-foreground group-hover:text-destructive transition-colors">
                    Emergency Services
                  </p>
                  <p className="text-lg font-bold text-destructive">911</p>
                </div>
              </a>
            </div>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col gap-2 px-6 pb-6">
          {onClose && (
            <Button 
              variant="outline"
              onClick={onClose}
              className="w-full"
              data-testid="escalation-modal-close-btn"
            >
              I understand, close this message
            </Button>
          )}
          <p className="text-xs text-center text-muted-foreground">
            Your safety is our priority
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default EscalationModal;
